import cx from 'classnames';
import {
    counter_ESHOPS,
    counter_VARIANTS,
    STROLLER_STROLLEROPEDIA,
} from 'mk/autogenerated/translations/GridStrollerItem.eb21fdfa8e548cb5276224ed8d250f61'
import { QUALITY_VALUES } from 'mk/bazaar/common/enums';
import ProductFacets from 'mk2/apps/bazaar/components/ProductFacets';
import ProductPrice from 'mk2/apps/bazaar/components/ProductPrice';
import { ProductQuality, XMLImportItemEntity } from 'mk2/apps/bazaar/schemas';
import { StrollerEntity, StrollerPhoto } from 'mk2/apps/strollers/schemas';
import { strollersDetailUrl, strollersDetailVariantUrl, strollersUrl } from 'mk2/apps/strollers/urls';
import { ImgCropped, ImgCropMode } from 'mk2/components/ImgCropped';
import { Link } from 'mk2/components/Link';
import { url } from 'mk2/helpers/urls';
import useEventCallback from 'mk2/hooks/useEventCallback';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import stylesGridItemFooter from './GridItemFooter.mscss';
import stylesGridItemHeader from './GridItemHeader.mscss';
import stylesGridItemInfo from './GridItemInfo.mscss';
import stylesGridItemPhoto from './GridItemPhoto.mscss';
import stylesGridStrollerItem from './GridStrollerItem.mscss';

interface OwnProps {
    className?: string;
    stroller: StrollerEntity;
    strollerEshopItems: XMLImportItemEntity[];
    style?: React.CSSProperties;
    strollerPhotosMap: Record<number, StrollerPhoto>;
    variantsPhotoIds: Record<number, number[]>;
    preferVariant: boolean;
    gridIndex: number;
    onClick?(stroller: StrollerEntity, variantId: number, gridIndex: number);
}

type Props = OwnProps;

const GridStrollerItem: React.FunctionComponent<Props> = ({
    className,
    stroller,
    strollerEshopItems,
    strollerPhotosMap,
    variantsPhotoIds,
    preferVariant,
    gridIndex,
    onClick,
}) => {
    const uniqueFeedIds = new Set();
    const uniqueVariantIds = new Set();
    let firstVariant = null;
    let firstPhoto = null;
    strollerEshopItems?.forEach((item) => {
        if (item.xmlImportFeed) {
            uniqueFeedIds.add(item.xmlImportFeed.id);
        }
        if (item.strollerVariant) {
            uniqueVariantIds.add(item.strollerVariant.id);
            if (!firstVariant) {
                firstVariant = item.strollerVariant;
            }
        }
    });

    const detailUrl = (uniqueVariantIds.size === 1 || preferVariant) && firstVariant
        ? url(strollersDetailVariantUrl, { slug: stroller.slug, variantId: firstVariant.id })
        : url(strollersDetailUrl, { slug: stroller.slug });

    if (
        stroller?.subVariantFeaturesConfig &&
        Object.keys(stroller.subVariantFeaturesConfig).length >= 1 &&
        firstVariant && variantsPhotoIds?.[firstVariant.id] &&
        strollerPhotosMap[variantsPhotoIds[firstVariant.id][0]]
    ) {
        firstPhoto = strollerPhotosMap[variantsPhotoIds[firstVariant.id][0]];
    } else if (firstVariant?.photos?.[0]?.variantPhoto?.photo) {
        firstPhoto = firstVariant.photos[0].variantPhoto.photo;
    } else if (stroller?.previewPhoto) {
        firstPhoto = stroller.previewPhoto;
    }

    const onClickCallback = useEventCallback(() => {
        if (onClick) {
            onClick(stroller, firstVariant?.id, gridIndex);
        }
    }, [onClick, stroller, firstVariant?.id]);

    return (
        <div className={cx(stylesGridStrollerItem.GridStrollerItem, className)} data-cy="bazaar-grid-item">
            <Link to={detailUrl} onClick={onClickCallback}>
                <div className={cx(stylesGridItemHeader.GridItemHeader, stylesGridItemHeader[`GridItemHeader--${ProductQuality.NEW}`])}>
                    <span className={cx(stylesGridItemHeader.GridItemHeader__quality, stylesGridItemHeader[`GridItemHeader__quality--${ProductQuality.NEW}`])}>
                        {QUALITY_VALUES[ProductQuality.NEW].label}
                    </span>
                </div>
            </Link>
            <Link to={detailUrl} className={stylesGridStrollerItem.GridStrollerItem__link} onClick={onClickCallback}>
                <div className={stylesGridItemInfo.GridItemInfo}>
                    <div className={stylesGridItemInfo.GridItemInfo__content}>
                        <div className={stylesGridItemInfo.GridItemInfo__title} title={stroller.name}>
                                {stroller.name}
                            <div className={stylesGridItemInfo.GridItemInfo__mask} />
                        </div>

                        <div className={stylesGridItemInfo.GridItemInfo__props}>
                            <ProductFacets className={stylesGridItemInfo.GridItemInfo__facets} facets={[stroller.brand.name, stroller.name]} small />
                            <div className={stylesGridItemInfo.GridItemInfo__mask} />
                        </div>
                    </div>
                </div>
            </Link>
            <Link to={detailUrl} onClick={onClickCallback}>
                <div className={cx(stylesGridItemPhoto.GridItemPhoto)}>
                    <div className={stylesGridItemPhoto.GridItemPhoto__box}>
                        {firstPhoto &&
                            <ImgCropped
                                imgUrl={firstPhoto.url720x720}
                                lazy={false}
                                imgWidth={firstPhoto.originalWidth}
                                imgHeight={firstPhoto.originalHeight}
                                mode={ImgCropMode.Embed}
                                width={233}
                                height={221}
                            />
                        }
                        <ProductPrice
                            className={stylesGridItemPhoto.GridItemPhoto__price}
                            price={stroller.minPricedEshopProduct}
                            priceTo={stroller.maxPricedEshopProduct}
                            noDecimals
                            subtitle={interpolate(counter_VARIANTS, {
                                counter: uniqueVariantIds.size,
                                count: uniqueVariantIds.size,
                            })}
                        />
                    </div>
                </div>
            </Link>
            <div className={cx(stylesGridItemFooter.GridItemFooter)}>
                <div className={stylesGridItemFooter.GridItemFooter__first}>
                    <Link to={strollersUrl} className={stylesGridItemFooter.GridItemFooter__username}>
                        {STROLLER_STROLLEROPEDIA}
                    </Link>
                </div>
                <div className={stylesGridItemFooter.GridItemFooter__second}>
                    <Link to={detailUrl} onClick={onClickCallback}>
                        {interpolate(counter_ESHOPS, {
                            counter: uniqueFeedIds.size,
                            count: uniqueFeedIds.size,
                        })}
                    </Link>
                    <div className={stylesGridItemFooter.GridItemFooter__mask} />
                </div>
            </div>
        </div>
    );
};

GridStrollerItem.displayName = 'GridStrollerItem';

export default React.memo<Props>(GridStrollerItem);
